import {
  addHours,
  endOfWeek,
  formatISO,
  isSameDay,
  isWithinInterval,
  startOfWeek,
} from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '../../../../core/hooks/useScreenSize';
import { useWindowBreakpoints } from '../../../../core/hooks/useWindowBreakpoints';
import { useUserStore } from '../../../Auth/stores/authStore';
import { useFetchOnboardingSchedule } from '../../../Onboarding/hooks/useFetchOnboardingSchedule';
import { useJoinOnboarding } from '../../../Onboarding/hooks/useJoinOnboarding';
import { mapTourToLiveEvent } from '../../../Onboarding/utils/mapTourToLiveEvent';
import { shouldShowOnboarding } from '../../../Onboarding/utils/shouldShowOnboarding';
import { itsAprovaLiveRecordAvailable } from '../../helpers/itsAprovaLiveRecordAvailable';
import { LiveClass, LiveClassDto } from '../../models/LiveClassDto.model';
import { useFetchLiveClasses } from './useFetchLiveClasses';
import { useRegisterToLiveClass } from './useRegisterToLiveClass';

const ONBOARDING_TYPE = 'onboarding';

/**
 * Mescla as lives de onboarding (já normalizadas para `LiveClassDto`) nos dias
 * das aulas ao vivo, agrupando por dia de calendário e reordenando por horário.
 */
function mergeOnboardingByDay(
  liveDays: LiveClassDto[],
  onboardingDays: LiveClassDto[]
): LiveClassDto[] {
  const sortByStart = (a: LiveClass, b: LiveClass) =>
    new Date(a.startDate).getTime() - new Date(b.startDate).getTime();

  const result: LiveClassDto[] = liveDays.map((day) => ({
    ...day,
    events: [...day.events],
  }));

  for (const onboardingDay of onboardingDays) {
    const target = result.find((day) =>
      isSameDay(new Date(day.day), new Date(onboardingDay.day))
    );

    if (target) {
      target.events.push(...onboardingDay.events);
      target.events.sort(sortByStart);
    } else {
      result.push({
        day: onboardingDay.day,
        events: [...onboardingDay.events].sort(sortByStart),
      });
    }
  }

  return result;
}

function useTimelineTab(origin: 'aprova-live' | 'challenge' = 'challenge') {
  const navigate = useNavigate();

  const user = useUserStore((state) => state.user);
  const showOnboarding = origin === 'aprova-live' && shouldShowOnboarding(user);
  const { joinOnboarding } = useJoinOnboarding();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedInterval, setSelectedInterval] = useState<Interval>(() => {
    return {
      start: startOfWeek(selectedDate, { weekStartsOn: 1 }),
      end: endOfWeek(selectedDate, { weekStartsOn: 1 }),
    };
  });

  const [allLiveClasses, setAllLiveClasses] = useState<LiveClassDto[]>();
  const [filteredLiveClasses, setFilteredLiveClasses] = useState<LiveClass[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<LiveClass | null>(null);

  const [isModalEventDetailsOpen, setIsModalEventDetailsOpen] = useState(false);

  const screesSize = useScreenSize();
  const windowBreakpoints = useWindowBreakpoints();

  const fetchLiveClasses = useFetchLiveClasses(
    formatISO(selectedInterval.start),
    formatISO(selectedInterval.end),
    origin
  );

  const onboardingSchedule = useFetchOnboardingSchedule(
    formatISO(selectedInterval.start),
    formatISO(selectedInterval.end),
    showOnboarding
  );

  const registerToLiveClass = useRegisterToLiveClass();

  const getLiveClassesBySelectedDate = useCallback((data: LiveClassDto[]) => {
    if (!data) {
      return [];
    }

    const day = data.find((liveClass) => {
      const liveClassDate = new Date(liveClass.day);

      return isSameDay(liveClassDate, selectedDate);
    });

    if (!day) {
      return [];
    }

    const sortedByStartDate = day.events.sort((a, b) => {
      return (
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );
    });

    return sortedByStartDate;
  }, [selectedDate]);

  useEffect(() => {
    if (!fetchLiveClasses.data) {
      return;
    }

    let merged = fetchLiveClasses.data;

    if (showOnboarding && onboardingSchedule.data) {
      const onboardingDays: LiveClassDto[] = onboardingSchedule.data.map(
        (day) => ({
          day: day.day,
          events: day.events.map(mapTourToLiveEvent),
        })
      );

      merged = mergeOnboardingByDay(fetchLiveClasses.data, onboardingDays);
    }

    setAllLiveClasses(merged);
    setFilteredLiveClasses(getLiveClassesBySelectedDate(merged));
  }, [fetchLiveClasses.data, onboardingSchedule.data, showOnboarding]);

  useEffect(() => {
    if (allLiveClasses) {
      setFilteredLiveClasses(getLiveClassesBySelectedDate(allLiveClasses));
    }
  }, [selectedDate, allLiveClasses]);

  function onSelectDate(date: Date, interval: Interval) {
    setSelectedDate(date);

    if (interval !== selectedInterval) {
      setSelectedInterval(interval);
    }
  }

  function onSelectItem(item: LiveClass) {
    setSelectedEvent(item);
    if (screesSize < windowBreakpoints.lg) {
      setIsModalEventDetailsOpen(true);
    }
  }

  async function handleOpenLiveClass(eventId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const flatedLiveClasses = allLiveClasses?.flatMap((liveClass) => {
          return liveClass.events;
        });

        const liveClass = flatedLiveClasses?.find(
          (liveClass) => liveClass._id === eventId
        );

        if (!liveClass) {
          return resolve();
        }

        // Onboarding usa o join dedicado (abre o Zoom e marca presença).
        if (liveClass.type === ONBOARDING_TYPE) {
          joinOnboarding(liveClass.id).then(() => resolve());
          return;
        }

        const itsHappeningNow = isWithinInterval(new Date(), {
          start: new Date(liveClass.startDate),
          end: new Date(liveClass.endDate),
        });

        const now = new Date();
        const ended = now > new Date(liveClass.endDate);

        const oneHourToStart =
          now > addHours(new Date(liveClass.startDate), -1) && !ended;

        if (itsHappeningNow || oneHourToStart) {
          registerToLiveClass.mutateAsync(liveClass.id).then((response) => {
            const link =
              'https://zoom.us/w/' +
              response.webinarId +
              '?tk=' +
              response.zoomToken;

            const win = window.open(link, '_blank');
            win?.focus();
            return resolve();
          })
          .catch(reject);
        } else {
          if (origin === 'challenge') {
            navigate(`/ao-vivo/desafio-60-dias/${liveClass.slug}`);
          } else {
            navigate(`/ao-vivo/aprova-live/${liveClass.slug}`);
          }

          resolve();
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  return {
    selectedDate,
    selectedInterval,
    selectedEvent,

    fetchLiveClasses,
    isLoadingLives: fetchLiveClasses.isLoading,
    onSelectDate,
    onSelectItem,
    filteredLiveClasses,

    isModalEventDetailsOpen,
    setIsModalEventDetailsOpen,

    isLoadingRegister: registerToLiveClass.isLoading,

    itsAprovaLiveRecordAvailable,
    handleOpenLiveClass,
  };
}
export { useTimelineTab };
